/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild, AfterViewInit,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { XdaStringsService } from '../../services/xda-strings.service';

@Component({
  selector: 'xrx-slider',
  templateUrl: './xrx-slider.component.html',
  styleUrls: ['./xrx-slider.component.less'],
  standalone: false,
})
export class XrxSliderComponent implements OnInit, AfterViewInit {
  @ViewChild('f', { static: true }) public sliderForm: NgForm;
  @ViewChild('xrxSlider', { static: false }) xrxSlider: ElementRef;
  @ViewChild('slideThumb', { static: false }) slideThumb: ElementRef;

  @Input() max = 10;
  @Input() min = 0;
  @Input() step = 1;
  @Input() value: number | string = 0;

  @Output() valueChange = new EventEmitter<any>();
  @Output() validation = new EventEmitter<boolean>();

  isDragging = false;
  maxHandlePos: number;
  position: number;
  rangeDimension: number;
  sliderInputError = '';
  toFixed: number;

  private xdaTranslatePipe: XdaTranslate;
  constructor(private xdaStringsService: XdaStringsService) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.rangeDimension = this.xrxSlider.nativeElement.offsetWidth;
    this.maxHandlePos = this.rangeDimension - 1;
    this.toFixed = String(this.step).replace('.', '').length - 1;

    // set value
    setTimeout(() => {
      this.position = this._getPositionFromValue(
        parseFloat(String(this.value)),
      );
    }, 100);
  }

  onPointerDown(e: PointerEvent): void {
    e.preventDefault();

    // Only respond to mouse main button clicks (usually the left button) / single touch event
    if (e.button && e.button !== 0) {
      return;
    }

    // enable dragging the handle
    this.isDragging = true;

    // set position when the the slider is tapped
    const pos = this._getRelativePosition(e);
    this._setPosition(pos);
  }

  onPointerMove(e: PointerEvent): void {
    e.preventDefault();

    // only set position if dragging is enabled
    if (!this.isDragging) return;
    const pos = this._getRelativePosition(e);
    this._setPosition(pos);
  }

  onPointerUp(e: PointerEvent): void {
    e.preventDefault();
    this.isDragging = false;
  }

  onSliderInputChange(): void {
    if (this._validate()) {
      const pos = this._getPositionFromValue(Number(this.value));
      this._setPosition(pos);
    }
  }

  onKeyDown(e: KeyboardEvent): void {
    let newVal;
    if (e.key === 'ArrowLeft') {
      newVal = Number(this.value) - Number(this.step);
    } else if (e.key === 'ArrowRight') {
      newVal = Number(this.value) + Number(this.step);
    }
    if (newVal !== undefined) {
      const pos = this._getPositionFromValue(newVal);
      this._setPosition(pos);
    }
  }

  // update slider when Enter key is pressed when the slider input is in focus
  onSliderInputKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      const pos = this._getPositionFromValue(Number(this.value));
      this._setPosition(pos);
    }
  }

  _validate(): boolean {
    let isValid = true;
    // reset error
    this.sliderInputError = '';
    this._clearError('slider-value');

    // verify that value is a number
    if (isNaN(parseFloat(String(this.value)))) {
      this.sliderInputError = 'ZZ_SDE_VALUE_MUST_BE_NUMBER';
      this._setError('slider-value');
      isValid = false;
      this.validation.emit(isValid);
      return isValid;
    } else if (
      parseFloat(String(this.value)) > this.max ||
      parseFloat(String(this.value)) < this.min
    ) {
      setTimeout(() => {
        this.sliderInputError = this.xdaTranslatePipe.transform(
          'SDE_VALUE_MUST_BE4',
          [String(this.min), String(this.max)],
        );
        this._setError('slider-value');
      }, 100);
      isValid = false;
      this.validation.emit(isValid);
      return isValid;
    }
    this.validation.emit(isValid);
    return isValid;
  }

  _setError(control: string) {
    const ctrl = this.sliderForm.form.get(control);
    if (ctrl) {
      setTimeout(() => {
        ctrl.setErrors({ customerror: true });
        ctrl.markAsTouched();
        ctrl.markAsDirty();
      }, 100);
    }
  }

  _clearError(control: string) {
    const ctrl = this.sliderForm.form.get(control);
    if (ctrl) {
      ctrl.setErrors({});
      ctrl.markAsPristine();
      ctrl.updateValueAndValidity();
    }
  }
  _getRelativePosition(e: PointerEvent): number {
    // Get the offset  relative to the viewport
    const rangePos =
      this.xrxSlider.nativeElement.getBoundingClientRect()['left'];
    let pageCoordinate = 0;

    if (typeof e.clientX !== 'undefined') {
      pageCoordinate = e.clientX;
    }

    return pageCoordinate - rangePos;
  }

  _setPosition(pos: number): void {
    // Snapping steps
    const value = this._getValueFromPosition(
      this._cap(pos, 0, this.maxHandlePos),
    );
    const newPos = this._getPositionFromValue(value);

    // Update globals
    this.position = newPos;
    this.value = value;

    // emit change event
    this.valueChange.emit(this.value);
  }

  _getValueFromPosition(pos: number): number {
    const percentage = pos / (this.maxHandlePos || 1);
    const value =
      this.step * Math.round((percentage * (this.max - this.min)) / this.step) +
      this.min;
    return Number(value.toFixed(this.toFixed));
  }

  _getPositionFromValue(value: number): number {
    const percentage = (value - this.min) / (this.max - this.min);
    const pos = !Number.isNaN(percentage) ? percentage * this.maxHandlePos : 0;
    return pos;
  }

  _cap(pos: number, min: number, max: number): number {
    // return min or max position values for position values outside of the min/max range
    if (pos < min) {
      return min;
    }

    if (pos > max) {
      return max;
    }

    return pos;
  }
}
